import { useEffect, useState } from "react";
import { useToggle } from "react-use";
import {
  FullWidthSpace,
  DesignTokens,
  Row,
  Col,
  Title,
  RangePicker,
  FormItem,
  Switch,
  Space,
} from "melco-ui";
import {
  ReportingMelcoMachineStatisticsModelsGroupListItem,
  useRequirePermissions,
} from "melco-shared-logic";
import { StatisticsConfig } from "../../../hooks/reporting/statistics/useStatistics";
import dayjs from "dayjs";
import InteractiveOverlay from "../../overlay/InteractiveOverlay";
import { EnableProReportingButton } from "../proReporting/EnableProReportingButton";
import { Filters } from "./filters/Filters";
import styled from "styled-components";
import { ChartsAndDetailedStatistics } from "./ChartsAndDetailedStatistics";
import { useTranslation } from "react-i18next";
import { useGetReportingFilter } from "../../../hooks/reporting/statistics/useGetReportingFilter";
import { useMachineGroupsWithAPIAction } from "../../../hooks/reporting/useMachineGroupsWithAPIAction";
import LoadingDefaultSpinIndicator from "../../loading/LoadingDefaultSpinIndicator";
const { spacings } = DesignTokens;

const StyledCol = styled(Col)`
  display: flex; /* Ensure flex properties work */
  align-items: flex-end; /* Align items to the bottom */
`;

const LoaderDiv = styled.div`
  position: relative;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const DisplayStatistics: React.FC = () => {
  const { t } = useTranslation();
  // const [grouped, toggleGrouped] = useToggle(false);
  const getReportingFilter = useGetReportingFilter();
  const getAllMachinesGroups = useMachineGroupsWithAPIAction();
  const [statisticsFilter, setStatisticsFilter] = useState<
    StatisticsConfig | undefined
  >({});

  const [loading, setLoading] = useState(true);
  const isMachineReportingAllowed = useRequirePermissions([
    "MACHINE_REPORTING",
  ]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const data = await getReportingFilter("Statistics");
        console.log("config saved data    ", JSON.stringify(data));
        const allMachineGroupsResponse = await getAllMachinesGroups();
        const selectedMachineGroupIds = data?.groupList
          ?.split(",")
          .map((s) => s.trim());
        const machineGroupsFilter = allMachineGroupsResponse?.list
          .filter(
            (g: ReportingMelcoMachineStatisticsModelsGroupListItem) =>
              g.id && selectedMachineGroupIds?.includes(g.id)
          )
          .map((g) => ({
            label: g.name!,
            value: g.id!,
          }));

        const designsFilter = data?.designList
          ?.split(",")

          .map((s) => s.trim())
          .filter(Boolean)
          .map((design) => ({
            label: design,
            value: design,
          }));

        const finalConfig: StatisticsConfig = {
          grouped: data?.isGrouped,
          startTimestamp: dayjs(data?.startDate).toDate(),
          endTimestamp: dayjs(data?.endDate).toDate(),
          machineGroupsFilter:
            machineGroupsFilter && machineGroupsFilter?.length > 0
              ? machineGroupsFilter
              : [],

          designsFilter: designsFilter,
        };

        console.log(finalConfig);
        setStatisticsFilter(finalConfig);
      } catch (err) {
        console.error("Failed to load config", err);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  if (loading || !statisticsFilter)
    return (
      <div>
        <LoaderDiv>
          <LoadingDefaultSpinIndicator
            isShown={true}
          ></LoadingDefaultSpinIndicator>
        </LoaderDiv>
      </div>
    );

  return (
    <FullWidthSpace
      direction="vertical"
      size="small"
      data-cy-id="dashboard-statistics"
    >
      <Row
        gutter={[spacings.middle, spacings.middle]}
        justify={"space-between"}
        align="bottom"
      >
        <Col>
          <Title level={4}>{t("reporting.statistics.title")}</Title>
        </Col>

        <StyledCol>
          {/* <FormItem
              label={t("reporting.statistics.switch")}
              layout="vertical"
            >
              <InteractiveOverlay
                isAllowed={isMachineReportingAllowed}
                actionComponent={<EnableProReportingButton text=" " />}
              >
                <Switch
                  checked={statisticsFilter?.grouped}
                  onChange={() => {
                    toggleGrouped();
                    setStatisticsFilter((prev) => ({
                      ...prev,
                      grouped: !prev?.grouped,
                    }));
                  }}
                />
              </InteractiveOverlay>
            </FormItem> */}
          <Space>
            <FormItem label="Date Range" layout="vertical">
              <RangePicker
                format="L"
                onChange={(value) => {
                  const startTimestamp =
                    (value && value[0] && value[0].startOf("day").toDate()) ??
                    undefined;
                  const endTimestamp =
                    (value && value[1] && value[1].endOf("day").toDate()) ??
                    undefined;

                  setStatisticsFilter((prevState) => ({
                    ...prevState,
                    startTimestamp,
                    endTimestamp,
                  }));
                }}
                value={
                  statisticsFilter?.startTimestamp &&
                  statisticsFilter?.endTimestamp && [
                    dayjs(statisticsFilter?.startTimestamp),
                    dayjs(statisticsFilter?.endTimestamp),
                  ]
                }
                allowClear={false}
              />
            </FormItem>
          </Space>
        </StyledCol>
      </Row>

      <Row gutter={[spacings.middle, spacings.middle]}>
        <Col span={24} xl={24} xxl={24}>
          <Filters
            statisticsFilter={statisticsFilter}
            setStatisticsFilter={setStatisticsFilter}
            isMachineReportingAllowed={isMachineReportingAllowed}
          />
        </Col>
        <Col span={24} xl={24} xxl={24}>
          <ChartsAndDetailedStatistics
            isMachineReportingAllowed={isMachineReportingAllowed}
            statisticsFilter={statisticsFilter}
          />
        </Col>
      </Row>
    </FullWidthSpace>
  );
};
